import React from 'react';
import {useGlobalConfig, useSectionContext} from '../../../context';

type UseLiveRegion = {
    isCountdownActive: boolean;
    hasCountdownJustExpired: boolean;
    initialCountdownStringRef: React.MutableRefObject<string | undefined>;
    initialTimeDistanceRef: React.MutableRefObject<number>;
};

export const isSameDay = (milliseconds: number) => {
    const now = new Date();
    const futureTime = new Date(now.getTime() + milliseconds);
    return now.toDateString() === futureTime.toDateString();
};

export const useLiveRegion = ({
    isCountdownActive,
    hasCountdownJustExpired,
    initialCountdownStringRef,
    initialTimeDistanceRef
}: UseLiveRegion) => {
    const sectionContext = useSectionContext();
    const {newContentOnPageNotification} = useGlobalConfig();

    const [liveRegionContent, setLiveRegionContent] = React.useState('');

    /*
        Announce live region when users lands on the page and the reveal is happening today but countdown hasn't expired yet
        Note: Announcing live regions on load does not work well with screen readers, therefore the timeout
    */
    React.useEffect(() => {
        if (!sectionContext) return;
        if (!isCountdownActive) return;

        const initialTimeDistance = initialTimeDistanceRef.current;
        const initialCountdownString = initialCountdownStringRef.current;

        let timeout: number;
        if (isSameDay(initialTimeDistance)) {
            timeout = setTimeout(() => {
                setLiveRegionContent(
                    `${newContentOnPageNotification} ${sectionContext.sectionAriaLabel}: ${initialCountdownString}`
                );
            }, 3000);
        }

        return () => clearTimeout(timeout);
    }, [
        sectionContext,
        isCountdownActive,
        newContentOnPageNotification,
        initialCountdownStringRef,
        initialTimeDistanceRef
    ]);

    // update live region content when the countdown expires while user is on the page
    React.useEffect(() => {
        const initialTimeDistance = initialTimeDistanceRef.current;

        // Prevents announcement if countdown was already expired when the page loaded
        if (!hasCountdownJustExpired || initialTimeDistance <= 0) return;
        if (!sectionContext) return;

        setLiveRegionContent(
            `${newContentOnPageNotification} ${sectionContext?.sectionAriaLabel}`
        );
    }, [
        sectionContext,
        hasCountdownJustExpired,
        newContentOnPageNotification,
        initialTimeDistanceRef
    ]);

    return liveRegionContent;
};
